// The DM row's × (Slack's "Close conversation"): server-synced (relay `dm_hidden`), cached here per
// user so the first paint already leaves hidden rows out. Imported directly, not via the app barrel.
import { client } from "./client.js";
import { dmRecency } from "./unread-selectors.js";
import { notificationState, readCursorState, unreadFlagState } from "../core/state.svelte.js";
import { reportUiFailure } from "../report-failure.js";
import { forgetLastChat } from "../last-chat.js";

const DM_HIDDEN_BASE = "cyborg7_dm_hidden";

// THE rule. Hidden while the hide is at least as new as the DM's newest message and nothing is
// unread (a red count always has a row to open, CYBORG-969). A muted DM stays hidden (mute's promise).
export function dmHiddenRule(
  hiddenAt: number | undefined,
  lastMessageAt: number,
  muted: boolean,
  badged: boolean,
): boolean {
  if (hiddenAt === undefined) return false;
  if (muted) return true;
  return hiddenAt >= lastMessageAt && !badged;
}

export class DmHiddenState {
  // workspaceId → (peerId → epoch ms the DM was hidden, relay clock).
  private _at: Record<string, Record<string, number>> = $state({});
  // Workspaces whose hidden set the relay has answered THIS session. The localStorage cache above is
  // not that answer (another device may have hidden a DM since), so it never sets this.
  private _loaded: Record<string, true> = $state({});
  private _boundUserId: string | null = null;

  private get key(): string | null {
    return this._boundUserId ? `${DM_HIDDEN_BASE}_${this._boundUserId}` : null; // null: unbound
  }

  bindUser(userId: string): void {
    this._boundUserId = userId;
    this._at = {};
    this._loaded = {};
    const key = this.key;
    if (!key || typeof localStorage === "undefined") return;
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? "null");
      if (parsed && typeof parsed === "object") this._at = parsed as typeof this._at;
    } catch {
      // intentional: best-effort hydrate; the next fetch_unread seeds the server's truth.
    }
  }

  clearLocal(): void {
    this._boundUserId = null;
    this._at = {};
    this._loaded = {};
  }

  // The relay's rows for one workspace REPLACE what this device held: the server is the truth.
  seed(workspaceId: string, hiddenAt: Record<string, number>): void {
    this._at = { ...this._at, [workspaceId]: { ...hiddenAt } };
    this._loaded = { ...this._loaded, [workspaceId]: true };
    this.persist();
  }

  isLoaded(workspaceId: string): boolean {
    return this._loaded[workspaceId] === true;
  }

  get(workspaceId: string, peerId: string): number | undefined {
    return this._at[workspaceId]?.[peerId];
  }

  set(workspaceId: string, peerId: string, hiddenAt: number | null): void {
    const ws = { ...this._at[workspaceId] };
    if (hiddenAt === null) delete ws[peerId];
    else ws[peerId] = hiddenAt;
    this._at = { ...this._at, [workspaceId]: ws };
    this.persist();
  }

  private persist(): void {
    const key = this.key;
    if (!key || typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(key, JSON.stringify(this._at));
    } catch {
      // intentional: best-effort cache of a server-held set; the next seed restores it.
    }
  }
}

export const dmHiddenState = new DmHiddenState();

// The ×. Marks the DM read (no red count left without a row) and hides it on every device.
// An older relay that refuses the call keeps the hide on this device only.
export async function hideDm(workspaceId: string, peerId: string, isGroup: boolean): Promise<void> {
  forgetLastChat(workspaceId, peerId); // before any await: the landing must not restore a DM just hidden
  const floor = dmRecency(workspaceId, peerId);
  const now = Date.now();
  dmHiddenState.set(workspaceId, peerId, Math.max(now, floor));
  notificationState.clear(workspaceId, peerId);
  if (isGroup) {
    unreadFlagState.clearUnread(workspaceId, peerId);
    readCursorState.advanceChannel(peerId, now);
    client.markRead(workspaceId, peerId, now);
  } else {
    readCursorState.advanceDm(peerId, now);
    client.markDmRead(workspaceId, peerId, now);
  }
  try {
    const { hiddenAt } = await client.setDmHidden(workspaceId, peerId, true);
    if (typeof hiddenAt === "number") {
      dmHiddenState.set(workspaceId, peerId, Math.max(hiddenAt, floor));
    }
  } catch (err) {
    reportUiFailure("sidebar.hide_dm", err, { workspaceId, peerId });
  }
}

// Opening a DM from anywhere (sidebar, search, +, a name tag, a mention, a profile) shows it again.
export function showDm(workspaceId: string, peerId: string): void {
  if (dmHiddenState.get(workspaceId, peerId) === undefined) return;
  dmHiddenState.set(workspaceId, peerId, null);
  client
    .setDmHidden(workspaceId, peerId, false)
    .catch((err: unknown) => reportUiFailure("dm.show_dm", err, { workspaceId, peerId }));
}

// The restore predicate for the landing (resolveLastChatRestore): a DM or group DM in the hidden set.
// Until the relay's set has loaded for this workspace a remembered DM / agent DM / group DM is UNKNOWN,
// and unknown is not restored: restoring it would showDm (un-hide) a DM hidden on another device.
// Channels cannot be hidden, so they restore at once.
export const lastChatHidden =
  (workspaceId: string, channels: readonly { id: string; type?: string }[] = []) =>
  (kind: string, id: string) => {
    if (dmHiddenState.get(workspaceId, id) !== undefined) return true;
    if (dmHiddenState.isLoaded(workspaceId)) return false;
    return kind !== "channel" || channels.some((c) => c.id === id && c.type === "group_dm");
  };
